import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AuditHeroStripComponent } from '../audit/audit-hero-strip.component';
import { AuditStamp } from '../audit/audit.models';

@Component({
  selector: 'app-page-detail-hero',
  standalone: true,
  imports: [CommonModule, AuditHeroStripComponent],
  template: `
    <section class="page-detail-hero">
      <div class="hero-main">
        @if (preferMedia) {
          <div class="hero-media">
            <ng-content select="[media]"></ng-content>
          </div>
        }
        @if (!preferMedia) {
        <div class="hero-icon">
          <i [class]="icon || 'pi pi-file'"></i>
        </div>
        }
        <div class="hero-title">
          @if (eyebrow) {
            <div class="hero-eyebrow">{{ eyebrow }}</div>
          }
          <h1>{{ title }}</h1>
          @if (subtitle) {
            <p>{{ subtitle }}</p>
          }
          <div class="hero-tags">
            <ng-content select="[tags]"></ng-content>
          </div>
        </div>
        <div class="hero-title-media">
          <ng-content select="[titleMedia]"></ng-content>
        </div>
      </div>
      <div class="hero-aside">
        <div class="hero-actions">
          <ng-content select="[actions]"></ng-content>
        </div>
        @if (auditStamp) {
          <app-audit-hero-strip [stamp]="auditStamp"></app-audit-hero-strip>
        }
      </div>
    </section>
  `,
  styles: [`
    .page-detail-hero {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.25rem;
      border: 1px solid var(--app-border, #dfe7f3);
      border-left: 4px solid var(--app-accent, #0ea5a3);
      border-radius: 8px;
      background: var(--app-surface, #ffffff);
      box-shadow: 0 10px 24px var(--app-shadow-soft, rgba(15, 23, 42, 0.05));
    }

    .hero-main,
    .hero-actions,
    .hero-tags,
    .hero-media,
    .hero-title-media {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .hero-main {
      min-width: 0;
      gap: 1rem;
    }

    .hero-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 4rem;
      height: 4rem;
      color: var(--app-accent-strong, #0f766e);
      background: var(--app-accent-surface, #ecfdf5);
      border-radius: 50%;
    }

    .hero-icon i {
      font-size: 2rem;
    }

    .hero-title {
      min-width: 0;
    }

    .hero-title-media:empty {
      display: none;
    }

    .hero-eyebrow {
      color: var(--app-accent-strong, #0f766e);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0;
      margin-bottom: 0.25rem;
      text-transform: uppercase;
    }

    h1 {
      color: var(--app-text, #172033);
      font-size: 2rem;
      line-height: 1.15;
      margin: 0;
    }

    p {
      color: var(--app-muted, #64748b);
      margin: 0.35rem 0 0;
    }

    .hero-tags {
      flex-wrap: wrap;
      margin-top: 0.75rem;
    }

    .hero-tags:empty {
      display: none;
    }

    .hero-actions {
      justify-content: flex-end;
      flex-wrap: wrap;
    }

    .hero-aside { display: flex; flex-direction: column; align-items: flex-end; gap: .7rem; min-width: 0; }

    :host ::ng-deep .hero-actions > [actions],
    :host ::ng-deep .hero-tags > [tags] {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    @media (max-width: 960px) {
      .page-detail-hero {
        align-items: stretch;
        flex-direction: column;
      }

      .hero-actions {
        justify-content: flex-start;
      }

      .hero-aside { align-items: flex-start; }

      :host ::ng-deep .hero-actions > [actions] {
        justify-content: flex-start;
      }
    }

    @media (max-width: 560px) {
      .page-detail-hero {
        padding: 0.85rem;
      }

      .hero-main {
        align-items: flex-start;
        flex-direction: column;
      }

      h1 {
        font-size: 1.55rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageDetailHeroComponent {
  @Input() eyebrow = '';
  @Input() title = '';
  @Input() subtitle = '';
  @Input() icon = '';
  @Input() preferMedia = false;
  @Input() auditStamp?: AuditStamp | null;
}
